import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { DEFAULT_BRAND_COLOR } from 'app/theme';
import { useThemeStore } from 'app/themeStore';
import { useAuthStore } from 'features/auth/stores/authStore';
import { Check, RotateCcw } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import {
  HeroContainer,
  HeroDecorCircleBottom,
  HeroDecorCircleTop,
  HeroSubtitle,
} from 'shared/components/HeroContainer';

import { useSettings } from '../hooks/useSettings';
import {
  CardHeaderRow,
  CardSubtext,
  CardTitle,
  CardTitleSpaced,
  CardTitleTight,
  ColorCircle,
  ColorPickerBox,
  ColorPickerRow,
  ColorPickerWrapper,
  ColorSwatch,
  ContentArea,
  DisabledFieldGroup,
  FieldGroupLabel,
  FieldHelperText,
  FieldLabel,
  FieldStack,
  FieldValue,
  HeroTitle,
  HeroTitleBlock,
  hexFieldSx,
  HexFieldColumn,
  HexInputRow,
  LoadingBox,
  PageRoot,
  ReadOnlyBadge,
  ResetButton,
  settingsFieldSx,
  settingsSelectSx,
  SettingsCard,
  SwatchGrid,
  TwoColumnRow,
} from '../settings.styles';
import { getTimePreview, THEME_SWATCHES, TIMEZONE_OPTIONS } from '../utils/settingsUtils';

// ─── Component ───────────────────────────────────────────────────────────────

const SettingsPage: React.FC = () => {
  const user = useAuthStore((s) => s.user);

  const accentColor = useThemeStore((s) => s.brandColor);
  const setAccentColor = useThemeStore((s) => s.setBrandColor);
  const [hexInput, setHexInput] = useState(accentColor.replace('#', ''));

  const { isLoading, updateSettings } = useSettings();

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleTimeFormatChange = (value: '12h' | '24h') => {
    updateSettings({ timeFormat: value });
  };

  const handleWeekStartChange = (value: 'monday' | 'sunday' | 'saturday') => {
    updateSettings({ weekStart: value });
  };

  const handleColorSelect = (color: string) => {
    setAccentColor(color);
    setHexInput(color.replace('#', ''));
  };

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9A-Fa-f]/g, '').slice(0, 6);
    setHexInput(raw);
    if (raw.length === 6) {
      setAccentColor('#' + raw);
    }
  };

  const handleColorReset = () => {
    handleColorSelect(DEFAULT_BRAND_COLOR);
  };

  const timePreview = useMemo(() => getTimePreview(new Date()), []);

  const isColorAtDefault = accentColor.toUpperCase() === DEFAULT_BRAND_COLOR.toUpperCase();

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <PageRoot>
      <HeroContainer padding="24px 28px">
        <HeroDecorCircleTop aria-hidden />
        <HeroDecorCircleBottom aria-hidden />
        <HeroTitleBlock>
          <HeroTitle>Settings</HeroTitle>
          <HeroSubtitle>Personalize ZeLog for how your team works.</HeroSubtitle>
        </HeroTitleBlock>
      </HeroContainer>

      <ContentArea>
        {isLoading ? (
          <LoadingBox>
            <CircularProgress size={32} />
          </LoadingBox>
        ) : (
          <>
            <SettingsCard>
              <CardTitleSpaced>Profile</CardTitleSpaced>
              <TwoColumnRow>
                <Box>
                  <FieldLabel>Full name</FieldLabel>
                  <FieldValue>{user?.name ?? '—'}</FieldValue>
                </Box>
                <Box>
                  <FieldLabel>Email</FieldLabel>
                  <FieldValue>{user?.email ?? '—'}</FieldValue>
                </Box>
                <Box>
                  <FieldLabel>Role</FieldLabel>
                  <FieldValue>
                    {user?.role
                      ? ({
                          Owner: 'Owner',
                          Admin: 'Admin',
                          ProjectManager: 'Project Manager',
                          TeamMember: 'Team Member',
                        }[user.role] ?? user.role)
                      : '—'}
                  </FieldValue>
                </Box>
              </TwoColumnRow>
            </SettingsCard>

            <SettingsCard>
              <CardHeaderRow>
                <CardTitle>Time settings</CardTitle>
                <ReadOnlyBadge>Read-only</ReadOnlyBadge>
              </CardHeaderRow>
              <DisabledFieldGroup>
                <FieldStack>
                  <TwoColumnRow>
                    <Box>
                      <FieldGroupLabel>Time format</FieldGroupLabel>
                      <FormControl fullWidth size="small" disabled>
                        <Select value="24h" IconComponent={KeyboardArrowDownIcon} sx={settingsSelectSx}>
                          <MenuItem value="24h">24-hour ({timePreview.h24})</MenuItem>
                          <MenuItem value="12h">12-hour ({timePreview.h12})</MenuItem>
                        </Select>
                      </FormControl>
                      <FieldHelperText>How times are displayed across the app.</FieldHelperText>
                    </Box>
                    <Box>
                      <FieldGroupLabel>Week starts on</FieldGroupLabel>
                      <FormControl fullWidth size="small" disabled>
                        <Select value="monday" IconComponent={KeyboardArrowDownIcon} sx={settingsSelectSx}>
                          <MenuItem value="monday">Monday</MenuItem>
                          <MenuItem value="sunday">Sunday</MenuItem>
                          <MenuItem value="saturday">Saturday</MenuItem>
                        </Select>
                      </FormControl>
                      <FieldHelperText>First day shown in weekly reports and calendars.</FieldHelperText>
                    </Box>
                  </TwoColumnRow>
                  <Box>
                    <FieldGroupLabel>Time zone</FieldGroupLabel>
                    <Autocomplete
                      options={TIMEZONE_OPTIONS}
                      value={TIMEZONE_OPTIONS.find((t) => t.value === 'Asia/Calcutta') ?? undefined}
                      getOptionLabel={(option) => option.label}
                      renderInput={(params) => (
                        <TextField {...params} size="small" fullWidth sx={settingsFieldSx} />
                      )}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      popupIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16, opacity: 0.6 }} />}
                      disableClearable
                      disabled
                    />
                    <FieldHelperText>Used for scheduling and time entry calculations.</FieldHelperText>
                  </Box>
                </FieldStack>
              </DisabledFieldGroup>
            </SettingsCard>

            <SettingsCard>
              <CardTitleTight>Theme color</CardTitleTight>
              <CardSubtext>
                Pick any color — we&apos;ll generate matching tints and apply them across the app.
              </CardSubtext>
              <ColorPickerRow>
                <Tooltip title="Choose theme color" placement="top" arrow>
                  <ColorPickerWrapper dotcolor={accentColor}>
                    <ColorCircle dotcolor={accentColor} />
                    <ColorPickerBox
                      type="color"
                      value={accentColor}
                      aria-label="Choose theme color"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleColorSelect(e.target.value)
                      }
                    />
                  </ColorPickerWrapper>
                </Tooltip>
                <HexFieldColumn>
                  <FieldGroupLabel>HEX code</FieldGroupLabel>
                  <HexInputRow>
                    <TextField
                      size="small"
                      value={hexInput}
                      onChange={handleHexInputChange}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">#</InputAdornment>,
                      }}
                      inputProps={{ maxLength: 6 }}
                      sx={hexFieldSx}
                    />
                    {!isColorAtDefault && (
                      <ResetButton
                        variant="outlined"
                        size="small"
                        onClick={handleColorReset}
                        startIcon={<RotateCcw size={12} />}
                      >
                        Reset
                      </ResetButton>
                    )}
                  </HexInputRow>
                </HexFieldColumn>
              </ColorPickerRow>
              <Box>
                <FieldGroupLabel>Presets</FieldGroupLabel>
                <SwatchGrid>
                  {THEME_SWATCHES.map((color) => {
                    const isSelected = accentColor.toUpperCase() === color.toUpperCase();
                    return (
                      <ColorSwatch
                        key={color}
                        swatchColor={color}
                        isSelected={isSelected}
                        onClick={() => handleColorSelect(color)}
                      >
                        {isSelected && <Check size={16} strokeWidth={3} color="#fff" />}
                      </ColorSwatch>
                    );
                  })}
                </SwatchGrid>
              </Box>
            </SettingsCard>
          </>
        )}
      </ContentArea>
    </PageRoot>
  );
};

export default SettingsPage;
